/*
  Alsunu — local-currency fixes for text the theme writes in USD.

  Shopify Markets converts product, cart and checkout prices by itself. What it
  cannot convert is copy: "Free shipping on orders over $80" lives in section
  settings and in the free-shipping bar, which compares a USD threshold with a
  cart total that is already in the shopper's currency.

  When the active currency is not the shop currency this script:
    • rewrites "$80" (the free-shipping threshold) in visible text to the local
      amount, rounded up to a whole unit so the promise is never understated;
    • drops "U.S. " in front of "orders" for shoppers outside the U.S., because
      the free-shipping rate applies to every country in the shipping zone;
    • recalculates the free-shipping bar from the converted threshold.
  A MutationObserver re-applies this to content that arrives later (cart drawer
  re-renders, quick add, section rendering).
*/
(() => {
  const S = window.Shopify || {};
  const active = S.currency && S.currency.active;
  const rate = parseFloat(S.currency && S.currency.rate);
  const shopCurrency = document.documentElement.dataset.alsShopCurrency || 'USD';
  const threshold = parseFloat(document.documentElement.dataset.alsFreeShip || '80');
  if (!active || !rate || active === shopCurrency) return;

  // Format like Shopify's own `money` filter: Liquid prints 1234.56 in the active
  // currency's format (e.g. "€1.234,56", "¥1,235"), we read separators and symbol from it.
  const sample = (code) => {
    const raw = document.documentElement.dataset[code ? 'alsMoneyCode' : 'alsMoney'] || '';
    const m = raw.match(/^(.*?)1(\D?)23[45](?:(\D)(\d{2}))?(.*)$/s);
    return m ? { pre: m[1], thou: m[2], dec: m[3] || '', digits: m[4] ? 2 : 0, post: m[5] } : null;
  };
  const fmt = (amount, digits, code) => {
    const f = sample(code);
    if (!f) return new Intl.NumberFormat('en', { style: 'currency', currency: active }).format(amount);
    const d = Math.min(digits, f.digits);
    const [i, frac] = amount.toFixed(d).split('.');
    const int = i.replace(/\B(?=(\d{3})+(?!\d))/g, f.thou);
    return f.pre + int + (d ? f.dec + frac : '') + f.post;
  };

  const localThreshold = Math.ceil(threshold * rate);
  const thresholdText = fmt(localThreshold, 0);
  const amountRe = new RegExp('\\$\\s?' + threshold + '(?![\\d.,])', 'g');
  const outsideUS = S.country && S.country !== 'US';

  const fixText = (root) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (n) =>
        n.parentElement && !n.parentElement.closest('script, style, noscript, textarea')
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT,
    });
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    for (const n of nodes) {
      let t = n.nodeValue;
      if (!t || (t.indexOf('$') === -1 && t.indexOf('U.S.') === -1)) continue;
      const before = t;
      t = t.replace(amountRe, thresholdText);
      if (outsideUS) t = t.replace(/\bU\.S\. orders\b/g, 'orders');
      if (t !== before) n.nodeValue = t;
    }
  };

  const fixBar = (bar) => {
    if (bar.dataset.alsLocal) return;
    bar.dataset.alsLocal = '1';
    const total = parseInt(bar.dataset.cartTotal, 10) || 0; // minor units, local currency
    const thresholdMinor = localThreshold * 100;
    const remaining = thresholdMinor - total;
    const pct = Math.min(100, Math.floor((total * 100) / thresholdMinor));
    const msg = bar.querySelector('.als-ship-bar__msg');
    const done = bar.querySelector('template.als-ship-bar__done');
    if (remaining > 0) {
      msg.classList.remove('als-ship-bar__msg--done');
      msg.innerHTML = '';
      msg.append("You're ");
      const strong = document.createElement('strong');
      strong.textContent = fmt(remaining / 100, 2, bar.dataset.withCode === 'true');
      msg.append(strong, ' away from free shipping');
    } else if (done) {
      msg.replaceWith(done.content.cloneNode(true));
    }
    const track = bar.querySelector('.als-ship-bar__track');
    const fill = bar.querySelector('.als-ship-bar__fill');
    if (track) track.setAttribute('aria-valuenow', pct);
    if (fill) fill.style.width = pct + '%';
  };

  const apply = (root) => {
    fixText(root);
    (root.querySelectorAll ? root.querySelectorAll('.als-ship-bar[data-cart-total]') : []).forEach(fixBar);
    if (root.matches && root.matches('.als-ship-bar[data-cart-total]')) fixBar(root);
  };

  const start = () => {
    apply(document.body);
    new MutationObserver((records) => {
      for (const r of records) {
        r.addedNodes.forEach((n) => {
          if (n.nodeType === 1) apply(n);
          else if (n.nodeType === 3 && n.parentElement) fixText(n.parentElement);
        });
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
